import { Box, Button, HStack, Text, VStack, useToast } from '@chakra-ui/react'
import { useState } from 'react'
import {
  formatFeScheduleWhen,
  parentLevelFeSchedules,
  scheduleChildTicketId,
} from '@/components/forms/parentTicket/feDispatchUtils'
import {
  additionalFeOrdinalLabel,
  isAdditionalFeRole,
  isPrimaryFeRole,
} from '@/components/forms/parentTicket/feDispatchRole'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import type { FieldEngineerSummary } from '@/store/parentTicket'
import type { FESchedule } from '@/types/parentTicket'
import OverviewSectionCard from './OverviewSectionCard'

function EngineerRow({
  label,
  engineer,
  whenLabel,
  childLabel,
}: {
  label: string
  engineer: FieldEngineerSummary
  whenLabel?: string | null
  childLabel?: string | null
}) {
  const colors = useThemeColors()
  const name = engineer.full_name ?? '—'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const initial =
    parts.length >= 2
      ? `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase()
      : (name.trim()[0] ?? '?').toUpperCase()

  return (
    <HStack align="flex-start" spacing={2}>
      <Box
        w="28px"
        h="28px"
        borderRadius="full"
        bg="blue.50"
        display="grid"
        placeItems="center"
        flexShrink={0}
        mt="1px"
      >
        <Text fontWeight="500" color="blue.600" fontSize="xs">
          {initial}
        </Text>
      </Box>
      <Box minW={0} flex="1">
        <Text fontSize="11px" color={colors.textSecondary} lineHeight="short">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="normal" color={colors.text} lineHeight="short">
          {name}
        </Text>
        {whenLabel ? (
          <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
            {whenLabel}
          </Text>
        ) : null}
        {childLabel ? (
          <Text fontSize="xs" color="blue.600" mt={0.5}>
            {childLabel}
          </Text>
        ) : null}
      </Box>
    </HStack>
  )
}

type Props = {
  ticketId?: string
  primaryEngineer?: FieldEngineerSummary | null
  additionalEngineer?: FieldEngineerSummary | null
  additionalEngineers?: FieldEngineerSummary[] | null
  /** @deprecated Prefer additionalEngineer */
  backupEngineer?: FieldEngineerSummary | null
  /** @deprecated Prefer additionalEngineers */
  backupEngineers?: FieldEngineerSummary[] | null
  feSchedules?: FESchedule[]
  tasks?: ChildTicketTask[]
  onManageSchedule?: () => void
  onFeVisitsCreated?: () => void | Promise<void>
}

export default function AssignedTeamPanel({
  ticketId,
  primaryEngineer,
  additionalEngineer,
  additionalEngineers,
  backupEngineer,
  backupEngineers,
  feSchedules = [],
  tasks = [],
  onManageSchedule,
  onFeVisitsCreated,
}: Props) {
  const colors = useThemeColors()
  const toast = useToast()
  const [creatingVisits, setCreatingVisits] = useState(false)

  const parentSchedules = parentLevelFeSchedules(feSchedules)
  const primarySchedule =
    parentSchedules.find((s) => isPrimaryFeRole(s.role)) ?? parentSchedules[0] ?? null
  const primaryUserId = primaryEngineer?.user_id ?? primarySchedule?.fe_user_id ?? null
  const additionalSchedules = parentSchedules.filter(
    (s) => isAdditionalFeRole(s.role) && s.fe_user_id !== primaryUserId,
  )
  const effectiveAdditionalEngineers = (
    (additionalEngineers?.length ? additionalEngineers : null) ??
    (backupEngineers?.length ? backupEngineers : null) ??
    (additionalEngineer ? [additionalEngineer] : null) ??
    (backupEngineer ? [backupEngineer] : [])
  ).filter((engineer) => engineer.user_id !== primaryUserId)

  const hasPrimary = Boolean(primaryEngineer || primarySchedule)
  const hasAdditional = effectiveAdditionalEngineers.length > 0 || additionalSchedules.length > 0
  const hasFeSchedule = hasPrimary || hasAdditional
  const tasksWithAssignee = tasks.filter((t) => t.assignee_name?.trim())
  const hasAssignedTeam = tasksWithAssignee.length > 0 || hasFeSchedule

  const taskTitleById = new Map(tasks.map((t) => [t.id, t.title || t.task_number || `Task #${t.id}`]))
  const orphanSchedules = parentSchedules.filter((s) => scheduleChildTicketId(s) == null)
  const showCreateMissingVisits = Boolean(ticketId) && orphanSchedules.length > 0

  const linkedChildLabel = (schedule?: FESchedule | null): string | null => {
    if (!schedule) return null
    const childId = scheduleChildTicketId(schedule)
    if (childId == null) return null
    const title = taskTitleById.get(childId)
    return title ? `Linked: ${title}` : `Linked FE Visit #${childId}`
  }

  const handleCreateMissingVisits = async () => {
    if (!ticketId) return
    setCreatingVisits(true)
    try {
      const res = await TicketService.createMissingFeVisits(ticketId)
      if (!res.success) {
        toast({
          title: 'Could not create FE Visit tasks',
          description: res.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }
      toast({
        title: 'FE Visit tasks created',
        description: res.message ?? 'Missing FE Visit tasks were created and linked.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      await onFeVisitsCreated?.()
    } finally {
      setCreatingVisits(false)
    }
  }

  return (
    <OverviewSectionCard title="Assigned team">
      {hasAssignedTeam ? (
        <VStack align="stretch" spacing={3}>
          {tasksWithAssignee.length > 0 ? (
            <Box>
              <Text fontSize="11px" fontWeight="600" color={colors.textMuted} textTransform="uppercase" mb={2}>
                Task assignees
              </Text>
              <VStack align="stretch" spacing={2}>
                {tasksWithAssignee.map((task) => (
                  <EngineerRow
                    key={task.id}
                    label={taskTitleById.get(task.id) ?? `Task #${task.id}`}
                    engineer={{
                      id: task.assigned_to ?? task.id,
                      full_name: task.assignee_name ?? '—',
                    }}
                  />
                ))}
              </VStack>
            </Box>
          ) : null}

          {hasFeSchedule ? (
            <Box
              {...(tasksWithAssignee.length > 0
                ? { pt: 2, borderTop: '0.5px solid', borderColor: colors.borderLight }
                : {})}
            >
              <Text fontSize="11px" fontWeight="600" color={colors.textMuted} textTransform="uppercase" mb={2}>
                FE schedule
              </Text>
              <VStack align="stretch" spacing={2}>
                {hasPrimary ? (
                  <EngineerRow
                    label="1st FE (Primary)"
                    engineer={
                      primaryEngineer ?? {
                        id: primarySchedule?.fe_user_id ?? 0,
                        full_name: primarySchedule?.field_engineer?.name ?? '—',
                      }
                    }
                    whenLabel={primarySchedule ? formatFeScheduleWhen(primarySchedule) : null}
                    childLabel={linkedChildLabel(primarySchedule)}
                  />
                ) : null}
                {Array.from({
                  length: Math.max(effectiveAdditionalEngineers.length, additionalSchedules.length),
                }).map((_, index) => {
                  const engineer = effectiveAdditionalEngineers[index]
                  const schedule = engineer?.user_id
                    ? additionalSchedules.find((row) => row.fe_user_id === engineer.user_id) ??
                      additionalSchedules[index]
                    : additionalSchedules[index]
                  return (
                    <EngineerRow
                      key={engineer?.user_id ?? engineer?.id ?? schedule?.id ?? index}
                      label={additionalFeOrdinalLabel(index)}
                      engineer={
                        engineer ?? {
                          id: schedule?.fe_user_id ?? 0,
                          full_name: schedule?.field_engineer?.name ?? '—',
                        }
                      }
                      whenLabel={schedule ? formatFeScheduleWhen(schedule) : null}
                      childLabel={linkedChildLabel(schedule)}
                    />
                  )
                })}
              </VStack>
            </Box>
          ) : null}

          {showCreateMissingVisits ? (
            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              w="100%"
              fontWeight="normal"
              isLoading={creatingVisits}
              onClick={() => void handleCreateMissingVisits()}
            >
              Create missing FE Visit tasks
            </Button>
          ) : null}

          {onManageSchedule ? (
            <Button size="sm" variant="outline" w="100%" fontWeight="normal" onClick={onManageSchedule}>
              Manage schedule
            </Button>
          ) : null}
        </VStack>
      ) : (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm" color={colors.textSecondary}>
            No assignees or field engineers assigned yet.
          </Text>
          {onManageSchedule ? (
            <Button size="sm" variant="outline" w="100%" fontWeight="normal" onClick={onManageSchedule}>
              Go to schedule & assign
            </Button>
          ) : null}
        </VStack>
      )}
    </OverviewSectionCard>
  )
}
